/**
 * BURGER PRIME - MAIN APPLICATION FACADE
 * Orchestrates all core modules and initializes the application
 *
 * This file has been refactored to use ES6 modules for better organization.
 * Each feature is now in its own module in the /core directory.
 */

// Import core modules
import { initializeRippleEffects } from './core/ripple-effects.js';
import { initializeScrollAnimations } from './core/scroll-animations.js';
import { initializeThemeToggle } from './core/theme-toggle.js';
import { initializePerformanceOptimizations } from './core/performance.js';
import { initializeErrorHandling } from './core/error-handler.js';
import { initializeAccessibility } from './core/accessibility.js';
import { initializeAnalytics } from './core/analytics.js';
import { initializeServiceWorker } from './core/service-worker-init.js';

// Global application state
const BurgerPrimeApp = {
    initialized: false,
    version: '1.1.0', // Incremented for refactored version
    modules: {
        rippleEffects: false,
        scrollAnimations: false,
        themeToggle: false,
        performance: false,
        errorHandling: false,
        accessibility: false,
        analytics: false,
        serviceWorker: false
    }
};

// Make BurgerPrimeApp globally accessible for debugging
window.BurgerPrimeApp = BurgerPrimeApp;

/**
 * Initialize the entire application
 */
function initializeApp() {
    if (BurgerPrimeApp.initialized) {
        console.warn('⚠️ Burger Prime App already initialized');
        return;
    }

    console.log('🍔 Initializing Burger Prime App v' + BurgerPrimeApp.version);

    try {
        // Initialize error handling first (to catch errors from other modules)
        initializeErrorHandling();
        BurgerPrimeApp.modules.errorHandling = true;

        // Initialize core UI features
        initializeRippleEffects();
        BurgerPrimeApp.modules.rippleEffects = true;

        initializeScrollAnimations();
        BurgerPrimeApp.modules.scrollAnimations = true;

        initializeThemeToggle();
        BurgerPrimeApp.modules.themeToggle = true;

        // Initialize performance optimizations
        initializePerformanceOptimizations();
        BurgerPrimeApp.modules.performance = true;

        // Initialize accessibility features
        initializeAccessibility();
        BurgerPrimeApp.modules.accessibility = true;

        // Initialize analytics (optional, only if gtag is available)
        initializeAnalytics();
        BurgerPrimeApp.modules.analytics = true;

        // Initialize service worker for PWA features (optional)
        initializeServiceWorker();
        BurgerPrimeApp.modules.serviceWorker = true;

        // Mark as initialized
        BurgerPrimeApp.initialized = true;

        console.log('✅ Burger Prime App initialized successfully');
        console.log('📦 Active modules:', Object.keys(BurgerPrimeApp.modules).filter(key => BurgerPrimeApp.modules[key]));

    } catch (error) {
        console.error('❌ Failed to initialize Burger Prime App:', error);
    }
}

/**
 * Initialize app when DOM is ready
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
} else {
    // DOM already loaded
    initializeApp();
}

// Export for potential external use
export { BurgerPrimeApp, initializeApp };
